import styled, {
  css,
  FlattenInterpolation,
  Interpolation,
  ThemedStyledProps,
  ThemeProps,
} from 'styled-components';
import {
  activeStyles,
  focusStyles,
  hoverStyles,
  selectorButtonStyles,
} from '../../../commonStyles/selectorButton';
import { twoLayeredBoxShadow } from '../../../commonStyles/styling';
import colors from '../../../helpers/colors';
import fonts from '../../../helpers/fonts';
import mediaQuery, { stylePerBreakpoint } from '../../../helpers/mediaQuery';
import spacing, { spacingWithBorder } from '../../../helpers/spacing';
import { Breakpoint, Theme } from '../../../theme';
import Icon from '../../Icon';
import { ButtonsPerRowByBreakpoint } from '.';

export const Wrapper = styled.div<WrapperProps>`
  padding: 0;
  width: 100%;
  border: none;
  display: flex;
  flex-wrap: ${(props) => (props.wrapItems ? 'wrap' : 'nowrap')};

  ${({ orientation, buttonsPerRow = {} }) =>
    ['horizontal', 'mobileVertical'].includes(orientation) &&
    stylePerBreakpoint((breakpoint) => ({ theme }) => {
      const { gutterPx } = theme.layout[breakpoint];
      return (breakpoint === 'mobile' && orientation === 'mobileVertical') ||
        !buttonsPerRow[breakpoint]
        ? css`
            width: 100%;
            margin: 0;
          `
        : css`
            width: calc(100% + ${gutterPx}px);
            margin: -${gutterPx}px 0 0 -${gutterPx}px;
          `;
    })}
`;

export type RadioPositioningProps = {
  orientation: Orientation;
  buttonsPerRow?: ButtonsPerRowByBreakpoint;
};

export type RadioLabelProps = {
  disabled?: boolean;
};

export type RadioItemProps = RadioPositioningProps &
  RadioLabelProps & { contentAlignment: ContentAlignment };

type RadioPositioningPropsStyle = FlattenInterpolation<
  ThemedStyledProps<RadioPositioningProps, Theme>
>;

type WrapperProps = {
  orientation: Orientation;
  buttonsPerRow?: ButtonsPerRowByBreakpoint;
  wrapItems?: boolean;
};

export enum Orientation {
  /* Horizontal on all devices */
  horizontal = 'horizontal',
  /* Vertical on mobile devices only (horizontal on other devices) */
  mobileVertical = 'mobileVertical',
  /* Vertical on all devices */
  vertical = 'vertical',
}

export enum ContentAlignment {
  left = 'left',
  center = 'center',
}

const stackedItemStyle = (spaces: number): FlattenInterpolation<ThemeProps<Theme>> => css`
  flex: 1 100%;
  &:not(:first-of-type) {
    margin: ${spacingWithBorder(spaces, 1)} 0 0 0;
  }
`;

export const activeRadioItemStyles = css`
  &:focus-visible,
  input:focus-visible + & {
    ${focusStyles}
  }

  input:checked + &,
  &:active {
    ${activeStyles}
  }

  input:checked:focus-visible + & {
    box-shadow: inset 0 0 0 1px ${colors.selectorHighlight},
      ${twoLayeredBoxShadow(colors.neutral08, colors.core02)};
  }

  // The hover state should appear on unchecked radio items
  input:not(:checked) + &:hover {
    ${hoverStyles}
  }

  // The active hover style should appear on checked radio items
  // that are not in focus
  input:checked:not(:focus-visible) + &:hover {
    box-shadow: inset 0 0 0 1px ${colors.selectorHighlight};
  }
`;

export const StyledIcon = styled(Icon)`
  margin-bottom: ${spacing(1)};
`;

export const RadioTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const alignedLeftStyle = css`
  justify-content: left;
  align-items: left;
  flex-direction: row;
  &&& {
    padding: ${spacing(3)};
  }

  div {
    text-align: left;
    margin: auto ${spacing(2)};
  }

  span {
    margin-bottom: 0;
  }
`;

export const centerAlignStyle = css`
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

export const calculateFlexBasis = (
  breakpoint: Breakpoint,
  buttonsPerRow?: number
): Interpolation<ThemeProps<Theme>> => ({ theme }) => {
  const { gutterPx } = theme.layout[breakpoint];
  return buttonsPerRow && `calc(${100 / buttonsPerRow}% - ${gutterPx}px)`;
};

export const inlineItemStyle = (
  breakpoint: Breakpoint,
  buttonsPerRow?: ButtonsPerRowByBreakpoint
): Interpolation<ThemeProps<Theme>> => ({ theme }) =>
  buttonsPerRow && buttonsPerRow[breakpoint]
    ? css`
        flex: 1 0 ${calculateFlexBasis(breakpoint, buttonsPerRow[breakpoint])};
        && {
          margin: ${theme.layout[breakpoint].gutterPx}px 0 0
            ${theme.layout[breakpoint].gutterPx}px;
        }
      `
    : css`
        flex: 1;

        &:not(:first-of-type) {
          margin: 0 0 0 ${theme.layout[breakpoint].gutterPx}px;
        }

        &:first-of-type {
          margin: 0;
        }
      `;

export const radioItemPositioning = (
  stackedItemSpacing: number
): RadioPositioningPropsStyle => css<RadioPositioningProps>`
  ${({ orientation }) =>
    ['vertical', 'mobileVertical'].includes(orientation) &&
    stackedItemStyle(stackedItemSpacing)}

  ${({ orientation, buttonsPerRow }) =>
    ['horizontal', 'mobileVertical'].includes(orientation) &&
    stylePerBreakpoint(
      (breakpoint) =>
        !(breakpoint === 'mobile' && orientation === 'mobileVertical') &&
        inlineItemStyle(breakpoint, buttonsPerRow ?? undefined)
    )}
`;

export const RadioItem = styled.label<RadioItemProps>`
  /* Layout */
  display: flex;

  ${radioItemPositioning(2)}

  ${selectorButtonStyles}

  ${({ disabled }) => (disabled ? '' : activeRadioItemStyles)}

  ${({ contentAlignment }) => ['left'].includes(contentAlignment) && alignedLeftStyle}

  ${({ contentAlignment }) => ['center'].includes(contentAlignment) && centerAlignStyle}
`;

export const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;
  pointer-events: none;
`;

export const Description = styled.p`
  ${fonts.paragraphSmall};
  width: 100%;
  margin: 0;

  ${mediaQuery.tabletLandscape`
    ${fonts.paragraph};
  `}
`;
